import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, LinearProgress, Tabs, Tab,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  RefreshCw, ShieldOff, ShieldCheck, Gift, Search,
  FileText, DollarSign,
} from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import { useTranslation } from "react-i18next";

const STATUS_COLORS = {
  active:    "#22C55E",
  past_due:  "#F59E0B",
  grace:     "#F59E0B",
  suspended: "#EF4444",
  pending:   "#8B5CF6",
  cancelled: "#6B7280",
};

function StatusChip({ status }) {
  const color = STATUS_COLORS[status] || "#6B7280";
  return (
    <Chip label={status || "—"} size="small"
      sx={{ fontWeight: 700, fontSize: "0.6875rem", height: 22, background: alpha(color, 0.12),
        color, border: `1px solid ${alpha(color, 0.3)}` }} />
  );
}

function MinBar({ used, limit, color }) {
  const { t } = useTranslation("admin");
  if (limit === -1) return <Typography sx={{ fontSize: "0.6875rem", color: "#22C55E" }}>{t("unlimited")}</Typography>;
  const pct = limit > 0 ? Math.min(100, Math.round(used / limit * 100)) : 0;
  const c = pct >= 100 ? "#EF4444" : pct >= 80 ? "#F59E0B" : color;
  return (
    <Box>
      <Typography sx={{ fontSize: "0.6875rem", fontVariantNumeric: "tabular-nums", color: c, fontWeight: 600 }}>
        {used} / {limit} min
      </Typography>
      <LinearProgress variant="determinate" value={pct}
        sx={{ height: 4, borderRadius: 2, mt: 0.25,
          "& .MuiLinearProgress-bar": { background: c }, backgroundColor: alpha(c, 0.12) }} />
    </Box>
  );
}

// ── Grant Quota Dialog ────────────────────────────────────────────────────────
function GrantQuotaDialog({ open, tenant, onClose, onDone }) {
  const { t }    = useTranslation("admin");
  const [slots,   setSlots]   = useState("5");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      await axiosInstance.post(`/admin/subscriptions/${tenant.subId}/grant-quota`, { slots: parseInt(slots) });
      onDone(t("grantQuotaTitle") + ": " + tenant.name);
    } catch (e) {
      setError(e.response?.data?.message || t("failedGrantQuota"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "0.9375rem" }}>{t("grantQuotaTitle")}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>{error}</Alert>}
        <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mb: 1.5 }}>
          {t("grantQuotaDesc", { name: tenant?.name })}
        </Typography>
        <TextField
          label={t("slotsToGrant")} type="number" size="small" fullWidth
          value={slots} onChange={(e) => setSlots(e.target.value)}
          inputProps={{ min: 1, max: 50 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "text.secondary" }}>{t("cancel")}</Button>
        <Button variant="contained" onClick={submit} disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600, background: "#4F46E5" }}>
          {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : t("grant")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Audit Log Tab ─────────────────────────────────────────────────────────────
function AuditLogTab() {
  const { t }     = useTranslation("admin");
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter,  setFilter]  = useState("");

  useEffect(() => {
    axiosInstance.get("/admin/audit-log?limit=100")
      .then(r => setRows(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter
    ? rows.filter(r => (r.action || "").includes(filter) || (r.userName || "").toLowerCase().includes(filter.toLowerCase()))
    : rows;

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", gap: 1.5, alignItems: "center" }}>
        <TextField size="small" placeholder={t("filterByActionOrUser")}
          value={filter} onChange={(e) => setFilter(e.target.value)}
          InputProps={{ startAdornment: <Search size={14} style={{ marginRight: 6, color: "#9CA3AF" }} /> }}
          sx={{ maxWidth: 320 }} />
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                {[t("colTime"), t("colUser"), t("colAction"), t("colEntity"), t("colIP")].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(row => (
                <TableRow key={row.id} sx={{ "&:hover": { background: alpha("#4F46E5", 0.035) } }}>
                  <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                    {new Date(row.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem" }}>
                    {row.userName || "—"}
                    {row.userEmail && <Typography sx={{ fontSize: "0.625rem", color: "text.disabled" }}>{row.userEmail}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip label={row.action} size="small"
                      sx={{ fontSize: "0.625rem", height: 20, fontWeight: 700,
                        background: row.action?.includes("suspend") ? alpha("#EF4444", 0.1)
                          : row.action?.includes("grant")   ? alpha("#22C55E", 0.1)
                          : alpha("#4F46E5", 0.08),
                        color: row.action?.includes("suspend") ? "#EF4444"
                          : row.action?.includes("grant")   ? "#22C55E" : "#4F46E5" }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                    {row.entityType}{row.entityId ? ` #${row.entityId}` : ""}
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.75rem", color: "text.disabled" }}>{row.ip || "—"}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 4, color: "text.disabled", fontSize: "0.875rem" }}>{t("noEntries")}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminBilling() {
  const { t }    = useTranslation("admin");
  const [tab,       setTab]       = useState(0);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [actionMsg, setActionMsg] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [grantTarget, setGrantTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axiosInstance.get("/admin/subscriptions?limit=50");
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (type, msg) => {
    if (type === "success") setActionMsg(msg);
    else setActionErr(msg);
    setTimeout(() => { setActionMsg(""); setActionErr(""); }, 5000);
  };

  const suspend = async (row) => {
    try {
      await axiosInstance.post(`/admin/subscriptions/${row.subId}/suspend`);
      flash("success", t("tooltipSuspend") + ": " + row.name);
      load();
    } catch (e) { flash("error", e.response?.data?.message || t("failedSuspend")); }
  };

  const unsuspend = async (row) => {
    try {
      await axiosInstance.post(`/admin/subscriptions/${row.subId}/unsuspend`);
      flash("success", t("tooltipReactivate") + ": " + row.name);
      load();
    } catch (e) { flash("error", e.response?.data?.message || t("failedUnsuspend")); }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.03em", color: "text.primary" }}>
            {t("billingAdminTitle")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
            {t("billingAdminSub")}
          </Typography>
        </Box>
        <Tooltip title={t("refresh")}>
          <Button size="small" onClick={load} disabled={loading}
            startIcon={<RefreshCw size={14} />}
            sx={{ textTransform: "none", color: "text.secondary", fontSize: "0.8125rem" }}>
            {t("refresh")}
          </Button>
        </Tooltip>
      </Box>

      {actionMsg && <Alert severity="success" sx={{ mb: 2.5, borderRadius: "12px" }} onClose={() => setActionMsg("")}>{actionMsg}</Alert>}
      {actionErr && <Alert severity="error"   sx={{ mb: 2.5, borderRadius: "12px" }} onClose={() => setActionErr("")}>{actionErr}</Alert>}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: "1px solid var(--line-200)" }}>
        <Tab icon={<DollarSign size={15} />} iconPosition="start" label={t("tabSubscriptions")}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.875rem", minHeight: 44 }} />
        <Tab icon={<FileText size={15} />} iconPosition="start" label={t("tabAuditLog")}
          sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.875rem", minHeight: 44 }} />
      </Tabs>

      {tab === 0 && (
        loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  {[t("colTenant"), t("colPlan"), t("colStatus"), t("colPeriodEnd"), t("colInbound"), t("colOutbound"), t("colDeployQuota"), t("colActions")].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.rows || []).map((row) => {
                  const inboundMin  = Math.ceil((row.inboundSeconds  || 0) / 60);
                  const outboundMin = Math.ceil((row.outboundSeconds || 0) / 60);
                  const deployUsed  = row.deployUsed  || 0;
                  const deployTotal = row.deployTotal || row.deployQuotaPerPeriod || 5;
                  const isSuspended = row.status === "suspended";

                  return (
                    <TableRow key={row.id} sx={{ "&:hover": { background: alpha("#4F46E5", 0.03) } }}>
                      <TableCell sx={{ minWidth: 160 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: "0.8125rem", color: "text.primary" }}>{row.name}</Typography>
                        <Typography sx={{ fontSize: "0.6875rem", color: "text.disabled" }}>{row.email}</Typography>
                        {row.enCompanyName && row.enCompanyName !== "-" && (
                          <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>{row.enCompanyName}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.planId ? (
                          <Chip label={row.planId} size="small"
                            sx={{ fontWeight: 700, fontSize: "0.6875rem", height: 22,
                              background: alpha("#4F46E5", 0.1), color: "#4F46E5" }} />
                        ) : <Typography sx={{ fontSize: "0.75rem", color: "text.disabled" }}>—</Typography>}
                      </TableCell>
                      <TableCell><StatusChip status={row.status} /></TableCell>
                      <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                        {row.currentPeriodEnd
                          ? new Date(row.currentPeriodEnd).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                          : "—"}
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <MinBar used={inboundMin}  limit={row.inboundMinutes  ?? -1} color="#4F46E5" />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <MinBar used={outboundMin} limit={row.outboundMinutes ?? -1} color="#06B6D4" />
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>
                        <Typography sx={{ fontSize: "0.6875rem", fontVariantNumeric: "tabular-nums",
                          color: deployUsed >= deployTotal ? "#EF4444" : "text.primary", fontWeight: 600 }}>
                          {t("deployUsedOf", { used: deployUsed, total: deployTotal })}
                        </Typography>
                        <LinearProgress variant="determinate"
                          value={deployTotal > 0 ? Math.min(100, deployUsed / deployTotal * 100) : 0}
                          sx={{ height: 4, borderRadius: 2, mt: 0.25,
                            "& .MuiLinearProgress-bar": { background: "#F59E0B" },
                            backgroundColor: alpha("#F59E0B", 0.12) }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {row.subId && (
                            <>
                              <Tooltip title={isSuspended ? t("tooltipReactivate") : t("tooltipSuspend")}>
                                <IconButton size="small"
                                  onClick={() => isSuspended ? unsuspend(row) : suspend(row)}
                                  sx={{ color: isSuspended ? "#22C55E" : "#EF4444" }}>
                                  {isSuspended ? <ShieldCheck size={15} /> : <ShieldOff size={15} />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t("tooltipGrantQuota")}>
                                <IconButton size="small" onClick={() => setGrantTarget(row)}
                                  sx={{ color: "#F59E0B" }}>
                                  <Gift size={15} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(data?.rows || []).length === 0 && (
                  <TableRow><TableCell colSpan={8} sx={{ textAlign: "center", py: 6, color: "text.disabled" }}>{t("noTenantsFound")}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )
      )}

      {tab === 1 && <AuditLogTab />}

      <GrantQuotaDialog
        open={Boolean(grantTarget)}
        tenant={grantTarget}
        onClose={() => setGrantTarget(null)}
        onDone={(msg) => { setGrantTarget(null); flash("success", msg); load(); }}
      />
    </Box>
  );
}
